/** Public one-click unsubscribe page - GET renders state, POST applies.
 *  Never requires login. Token comes from the List-Unsubscribe URL in every marketing email.
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, XCircle, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UnsubscribePage = () => {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true });
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    axios.get(`${API}/marketing/unsubscribe/${token}`)
      .then((r) => setState({ loading: false, ...r.data }))
      .catch(() => setState({ loading: false, found: false }));
  }, [token]);

  const apply = async () => {
    setApplying(true);
    try {
      await axios.post(`${API}/marketing/unsubscribe/${token}`);
      setState((s) => ({ ...s, already_unsubscribed: true }));
    } catch { /* no-op - still shows a UI outcome */ }
    finally { setApplying(false); }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6" data-testid="unsubscribe-page">
      <div className="max-w-md w-full bg-card rounded-2xl border border-border p-8 text-center">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary/10 mb-4">
          <Mail className="w-7 h-7 text-primary" />
        </div>

        {state.loading ? (
          <div className="text-muted-foreground inline-flex items-center gap-2"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>
        ) : !state.found ? (
          <>
            <XCircle className="w-8 h-8 text-destructive mx-auto mb-2" />
            <h1 className="text-xl font-bold mb-2">Link not recognised</h1>
            <p className="text-sm text-muted-foreground">This unsubscribe link is invalid or expired. If you'd still like to be removed, reply to any email from us - a real human handles it.</p>
          </>
        ) : state.already_unsubscribed ? (
          <>
            <CheckCircle2 className="w-8 h-8 text-success mx-auto mb-2" />
            <h1 className="text-xl font-bold mb-2">You're unsubscribed</h1>
            <p className="text-sm text-muted-foreground"><strong>{state.email}</strong> won't receive any more marketing emails from PassADC. Transactional messages (like password resets) may still be sent if you have an account.</p>
          </>
        ) : (
          <>
            <h1 className="text-xl font-bold mb-2">Unsubscribe from PassADC?</h1>
            <p className="text-sm text-muted-foreground mb-6">One click removes <strong>{state.email}</strong> from all marketing emails. You can always resubscribe from your account later.</p>
            <Button onClick={apply} disabled={applying} data-testid="unsubscribe-confirm" className="w-full rounded-full">
              {applying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Yes, unsubscribe me
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default UnsubscribePage;
